<script setup lang="ts">
import { computed, ref, toRef, markRaw, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { i18n } from '@/i18n';
import { useInventoryStore } from '../../pages/useInventoryStore/';
import { InventoryTableViewModel } from '../../pages/inventoryTableViewModel';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { OrderRepository } from '@/coreInfrastructureModule/Repositories/OrderRepository';
import SvgLoader from '@/utils/SvgLoader.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import TableMemoButton from '@/corePagesModule/tableMemoButton/TableMemoButton.vue';
import ArrowIcon from '@/corePagesModule/BaseTableContent/components/ArrowIcon.vue';
import {
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  DETAIL_BUTTON_COLUMN_WIDTH,
  HEADER_MODIFY_BUTTON_COLUMN_WIDTH,
} from '@/corePagesModule/BaseTableContent/constants';
import { VIRTUAL_SCROLL, ORDER_MODE_TABLE_CONTENT_ID, ORDER_HEADER_COLUMN_DEFAULT_NUMBER } from '../../constants';
import { getMonthFromHeaderKey } from '../../utils/tableHelpers';
import { IParentBusiness } from '../../domains/entities/Business';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import { isLoading, isZeroMatch } from '@/coreDomainsModule/uiLoadingState';
import { InventoryTableService } from '../../domains/services/inventoryTableService';
import { diContainer } from '@/coreContainer/container';
import VirtualizedEzTable from '@/corePagesModule/components/ezTable/VirtualizedEzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzTableLoadingState from '@/corePagesModule/components/ezTable/EzTableLoadingState.vue';
import EzTableErrorState from '@/corePagesModule/components/ezTable/EzTableErrorState.vue';
import EzTableZeroMatchState from '@/corePagesModule/components/ezTable/EzTableZeroMatchState.vue';
import { useColumnResizer } from '@/corePagesModule/composables/useColumnResizer';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { useTableMemo } from '../../composables/useTableMemo';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import { useTableCalculation } from '@/InventoryControlModule/composables/useTableCalculation';
import { useOrderCalculation } from '@/InventoryControlModule/composables/useOrderCalculation';
import { TableCommonViewModel } from '@/viewModels/tableContent';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { useTableState } from '../../composables/useTableState';
import OrderMonthlyColumn from './OrderMonthlyColumn.vue';
import TransactionSummaryColumn from './TransactionSummaryColumn.vue';
import EditableForecastAccuracyCell from '../ForecastAccuracy/EditableForecastAccuracyCell.vue';
import ForecastAccuracyHistoryModal from '../ForecastAccuracy/ForecastAccuracyHistoryModal.vue';
import BusinessRowMenu from './BusinessRowMenu.vue';
import CombineOrderModal from '../CombineOrder/CombineOrderModal.vue';
import CombineOrderConfirmModal from '../CombineOrder/CombineOrderConfirmModal.vue';

/**
 * InventoryOrderTableコンポーネントのプロパティ
 * @param tableContainerWidth - テーブルコンテナの幅
 * @param tableContainerHeight - テーブルコンテナの高さ
 * @param isEllipsis - テキスト省略有無
 * @param mouseoverTextLabel - マウスオーバー時のテキストラベル処理関数
 */
interface InventoryOrderTableProps {
  tableContainerWidth: number;
  tableContainerHeight: number;
  isEllipsis: boolean;
  transactionMonths: number[];
  mouseoverTextLabel: (e: MouseEvent) => void;
}
const props = defineProps<InventoryOrderTableProps>();

// Store
const inventoryStore = useInventoryStore();
const { state } = storeToRefs(inventoryStore);
const {
  changeHeaderSorting,
  toggleOpenBusinesses,
  onCheckedAllRows,
  confirmOrderEdit,
  updateForecastAccuracySettingAccuracy,
  openForecastAccuracyHistoryModal,
  closeForecastAccuracyHistoryModal,
  changeForecastAccuracyHistoryPeriod,
  setBusinessRowMenuStateAndProcessing,
  getHiddenTransactionHeader,
} = inventoryStore;

const { getHiddenHeaderNumber, getMonthlyData } = useTableCalculation();

// 発注計算のComposable
const {
  calculateOrderModeExceptedEndInventory: calcOrderExceptedEnd,
  calculateOrderModeTurnOver: calcOrderTurnOver,
  getMonthFromHeaderKey: getMonthFromKey,
  ensureOrderEditData: ensureOrderData,
} = useOrderCalculation();

// Memo機能のComposable
const { saveAggregateMemo, deleteAggregateMemo, saveChildMemo, deleteChildMemo } = useTableMemo();

// テーブル状態管理のComposable
const {
  tableTextStyleClass,
  tableNumberStyleClass,
  getNowInventoryStatusClass,
  isNegative,
  handleToggle: baseHandleToggle,
  createScrollHandler,
} = useTableState();

// i18n
const t = i18n.t;

// ViewModels
const vm = markRaw(new TableCommonViewModel());
const inventoryVm = markRaw(new InventoryTableViewModel());

// 仮想スクロール設定
const ITEM_HEIGHT = VIRTUAL_SCROLL.ITEM_HEIGHT;
const BUFFER_SIZE = VIRTUAL_SCROLL.BUFFER_SIZE;

/**
 * 動的に計算されるコンテナ高さ（propsから取得）
 */
const containerHeight = computed(() => props.tableContainerHeight || 1000);

// 処理負荷軽減のためのキャッシュマップ
const styleClassCache = new Map<string, string>();
const headerDisplayCache = new Map<string, string>();
const executionDateCache = new Map<string, string>();

/**
 * テーブルのヘッダ（チェックボックス列を含む完全版）
 */
type FullHeader = {
  key: string;
  label: string;
  width: string;
  sticky: boolean;
  resizable?: boolean;
};

const defaultFullHeaderKeys = computed(() => {
  const exceptHeaders = new Set(['choice', 'Arrivals', 'orderConfirmation', 'headerModify']);
  const returnHeaders: string[] = [
    'checkbox',
    'orderDetail',
    ...vm.displayHeaders.inventoryControl.filter((key) => !exceptHeaders.has(key)),
  ];

  // 月次データヘッダー（一度だけ計算）
  const monthlyHeaders = props.transactionMonths.flatMap((month) =>
    inventoryVm.getMonthlyTransactionHeaderKeys(month, true).map((arrivalKey) => `transaction-${month}-${arrivalKey}`)
  );

  returnHeaders.push(...monthlyHeaders, 'headerModify');
  return returnHeaders;
});

const { headerList, updateDisplayHeader } = useTableHeader(
  defaultFullHeaderKeys,
  'inventoryControlWithOrder',
  inventoryVm.DEFAULT_HIDDEN_HEADER_KEYS
);

/**
 * 完全なヘッダー定義を生成する（チェックボックス列・基本ヘッダー・月次データ列・発注詳細・ヘッダー追加を含む）
 * @returns FullHeader[]
 */
const fullHeaders = computed(() => {
  console.log(headerList.value);

  const returnHeaders: FullHeader[] = [];
  // チェックボックス列
  returnHeaders.push(
    {
      key: 'checkbox',
      label: '',
      width: '50px',
      sticky: true,
      resizable: false,
    },
    {
      key: 'orderDetail',
      label: t('詳細'),
      width: `${DETAIL_BUTTON_COLUMN_WIDTH}px`,
      sticky: true,
      resizable: false,
    }
  );

  const exceptHeader = ['choice', 'Arrivals', 'orderConfirmation', 'headerModify'];

  const baseHeader = vm.displayHeaders.inventoryControl.filter(
    (key) => !exceptHeader.includes(key) && (headerList.value.length > 0 ? headerList.value.includes(key) : true)
  );

  // 基本ヘッダー
  for (const header of baseHeader) {
    const label = inventoryVm.getDisplayHeaderName(header);
    if (label === '') continue;
    returnHeaders.push({
      key: header,
      label,
      width: `${state.value.tableInfos[header] || MIN_COLUMN_WIDTH}px`,
      sticky: true,
      resizable: true,
    });
  }

  // 月次データヘッダー（編集カラムも含める）
  for (const month of props.transactionMonths) {
    for (const arrivalKey of inventoryVm.getMonthlyTransactionHeaderKeys(month, true)) {
      const key = `transaction-${month}-${arrivalKey}`;
      if (!headerList.value.includes(key)) continue;
      const labelMonth =
        arrivalKey !== 'nowInventory' &&
        arrivalKey !== 'previousMonthAccuracy' &&
        arrivalKey !== 'settingAccuracy' &&
        arrivalKey !== 'accuracyHistory'
          ? t(DateService.getFormattedMonth(month)) + '\n'
          : '';
      returnHeaders.push({
        key,
        label: `${labelMonth}${inventoryVm.getTransactionHeaderNameForOrderMode(arrivalKey)}`,
        width: `${state.value.tableInfos[key] || MIN_COLUMN_WIDTH}px`,
        sticky: false,
        resizable: arrivalKey !== 'alert',
      });
    }
  }

  // ヘッダー追加列
  returnHeaders.push({
    key: 'headerModify',
    label: t('ヘッダー追加'),
    width: `${HEADER_MODIFY_BUTTON_COLUMN_WIDTH}px`,
    sticky: false,
    resizable: false,
  });
  return returnHeaders;
});

const getModifiableHeaderList = computed(() => {
  return defaultFullHeaderKeys.value
    .filter((key) => !['checkbox', 'orderDetail', 'headerModify'].includes(key))
    .map((key) => {
      if (vm.displayHeaders.inventoryControl.includes(key)) {
        return { key: key, text: inventoryVm.getDisplayHeaderName(key), isShown: headerList.value.includes(key) };
      }
      const splitKey = key.split('-');
      const arrivalKey = splitKey.at(-1);
      const arrivalMonth = getMonthFromKey(key);
      if (!splitKey || !arrivalKey) return { key: key, text: 'ERROR', isShown: headerList.value.includes(key) };
      const labelText = inventoryVm.getTransactionHeaderNameForOrderMode(arrivalKey);
      const labelMonth =
        arrivalKey !== 'nowInventory' &&
        arrivalKey !== 'previousMonthAccuracy' &&
        arrivalKey !== 'settingAccuracy' &&
        arrivalKey !== 'accuracyHistory'
          ? t(DateService.getFormattedMonth(arrivalMonth)) + '\n'
          : '';
      return {
        key: key,
        text: `${labelMonth}${labelText}`,
        isShown: headerList.value.includes(key),
      };
    });
});

/**
 * 後方互換性のためのheaders（既存コードとの互換性を保つ）
 */
const headers = computed(() => {
  return fullHeaders.value.slice(1); // チェックボックス列を除く
});

/**
 * グリッドテンプレート列の配列を生成する
 */
const gridTemplateColumns = computed(() => {
  // fullHeadersのwidthのみを使う
  return fullHeaders.value.map((header) => header.width);
});

/**
 * 固定する列のインデックス配列を生成する
 */
const stickyColumnIndexes = computed(() => {
  return fullHeaders.value.map((header, index) => (header.sticky ? index : -1)).filter((index) => index !== -1);
});

/**
 * 展開されたアイテムのセット
 */
const expandedItems = computed(() => {
  const expanded = new Set<string>();
  if (state.value.isOpeningBusinessId) {
    expanded.add(state.value.isOpeningBusinessId);
  }
  return expanded;
});

/**
 * 子アイテムを取得する関数
 * @param item - 親ビジネスアイテム
 * @returns 子アイテムの配列
 */
const getChildItems = (item: IParentBusiness) => {
  return item.children || [];
};

/**
 * 列の左位置を正確に計算する
 * @param columnIndex - 計算対象の列インデックス
 * @returns 左位置のピクセル値（例: '50px'）
 */
const getLeftPosition = (columnIndex: number): string => {
  let leftPos = 0;
  for (let i = 0; i < columnIndex; i++) {
    if (fullHeaders.value[i] && fullHeaders.value[i].sticky) {
      const width = parseInt(fullHeaders.value[i].width.replace('px', ''), 10) || 0;
      leftPos += width;
    }
  }
  return `${leftPos}px`;
};

/**
 * 仮想スクロールのスクロールハンドラ
 * @param scrollTop - スクロール位置
 */
const handleScroll = (scrollTop: number) => {
  // オーダーテーブル特有の処理があれば追加
};

/**
 * VirtualizedEzTable用のtoggleハンドララッパー
 * @param itemId - 展開対象のID
 */
const handleToggleOpenBusinesses = async (itemId: string) => {
  await toggleOpenBusinesses(itemId);
};

// テーブル要素のrefを宣言
const tableEl = ref<HTMLElement | null>(null);

// =====================
// ヘッダー編集機能の追加
// =====================

// setupMoveWarehouse, safeConvertIDateToDateをグローバル関数として定義

/**
 * 置き場移動セットアップ
 * @param inventoryRepository - 在庫リポジトリ（使用されませんが、既存のAPIとの互換性のため保持）
 * @param business - ビジネス情報
 * @param targetYearMonth - 対象年月
 */
function setupMoveWarehouse(inventoryRepository: any, business: any, targetYearMonth: any) {
  return inventoryStore.setupMoveWarehouse(diContainer.inventoryRepository, business, targetYearMonth);
}

/**
 * 対象の親ビジネス（IParentBusiness）をbusinessListから取得する
 * @param businessId - 取引ID
 * @returns IParentBusiness | undefined
 */
function findParentBusinessById(businessId: string): IParentBusiness | undefined {
  return state.value.businessList.find((b) => b.businessId === businessId);
}

/**
 * 現在のテーブルローディング状態をEzTable用の型に変換
 */
const currentLoadingStatus = computed((): LoadingStatus => {
  const tableStatus = state.value.tableLoadingState.status;

  if (isLoading(tableStatus) || headerList.value.length === 0) {
    return { type: 'loading' };
  }

  if (tableStatus.type === 'error') {
    return {
      type: 'error',
      errorMessage: tableStatus.errorMessage || t('データの取得に失敗しました。再度お試しください。'),
    };
  }

  if (isZeroMatch(tableStatus) && state.value.businessList.length === 0) {
    return { type: 'zero-match' };
  }

  return { type: 'success' };
});

/**
 * 月別の最後のヘッダーキー
 */
const getMonthlyEndHeader = computed(() => {
  return props.transactionMonths.reduce((acc, month) => {
    acc[month] = inventoryVm
      .getMonthlyTransactionHeaderKeys(month, true)
      .filter((key) => !state.value.hiddenTransactionHeaderList.includes(`transaction-${month}-${key}`))
      .at(-1);
    return acc;
  }, {});
});

/**
 * ヘッダーキーがある月の最後のヘッダーと一致しているか(=ヘッダーの右にボーダーが出るか)を返す関数
 * @param headerKey - ヘッダーキー
 */
const hasHeaderRightBorder = (headerKey: string) => {
  return props.transactionMonths.some((month) => headerKey === `transaction-${month}-${getMonthlyEndHeader.value[month]}`);
};

const { onMousedownResizer } = useColumnResizer({
  tableType: 'inventoryControlWithOrder',
  keys: fullHeaders.value.map((header) => header.key),
  widthInfo: toRef(state.value, 'tableInfos'),
  tableEl, // 追加: テーブル要素のrefを渡す
});

/**
 * 子取引をリロードする(エラー時)
 * @param id - 親取引ID
 */
const reloadChildBusiness = async (id?: string) => {
  if (!id) return;
  await inventoryStore.fetchChildBusinesses(id);
};

/**
 * 仮想アイテムの型定義
 */
interface VirtualItem {
  type: 'loading' | 'error' | 'zeroMatch' | 'parent' | 'child';
  data?: IParentBusiness;
  parentId?: string;
}

/**
 * セルがハイライトされているかどうかを判定する
 * @param virtualItem - 仮想アイテム
 * @returns ハイライト状態
 */
const isCellHighlighted = (virtualItem: VirtualItem): boolean => {
  return (
    virtualItem.type === 'child' || // 子取引行
    virtualItem.type === 'loading' || // 子取引のロード行
    (state.value.isOpeningBusinessId === virtualItem.data?.businessId &&
      (virtualItem.data?.childrenAsyncStatus === 'loading' || virtualItem.data?.childrenAsyncStatus === 'loaded')) || // 展開中の親取引
    (virtualItem.data?.businessId ? state.value.checkedAggregateBusinessIds.includes(virtualItem.data.businessId) : false) || // チェックボックスが選択されている親取引
    (virtualItem.parentId ? state.value.checkedAggregateBusinessIds.includes(virtualItem.parentId) : false) // チェックボックスが選択されている子取引
  );
};

/**
 * 固定する列のleft値を算出する
 * @param headerIndex - 当該列のヘッダー番号
 */
const calcLeftNumber = (headerIndex: number) => {
  if (!fullHeaders.value[headerIndex].sticky) return 0;
  return stickyColumnIndexes.value
    .filter((index) => index < headerIndex)
    .reduce((acc, index) => {
      const currentWidth = parseFloat(fullHeaders.value[index].width);
      return acc + (isNaN(currentWidth) ? 0 : currentWidth);
    }, 0);
};

/**
 * ヘッダーの表示状態を更新する関数
 * ヘッダーの表示状態を更新し、state.value.hiddenTransactionHeaderListを更新する
 * @param key - ヘッダーキー
 * @param isHide - ヘッダーを非表示にするかどうか
 */
const updateTableHeaderData = async (key: string, isHide: boolean): Promise<void> => {
  // ヘッダーの表示状態を更新
  await updateDisplayHeader(key, isHide);
  // 非表示のトランザクションヘッダーリストを更新
  await getHiddenTransactionHeader();
};

defineExpose({
  state,
  t,
  changeHeaderSorting,
  handleScroll,
  toggleOpenBusinesses,
  onCheckedAllRows,
  diContainer,
  headers,
  getLeftPosition,
  InventoryTableService,
  getHiddenHeaderNumber,
});

/**
 * コンポーネントのクリーンアップ処理
 * メモリリークを防ぐためにキャッシュをクリア
 */
onUnmounted(() => {
  styleClassCache.clear();
  headerDisplayCache.clear();
  executionDateCache.clear();
});
</script>

<template>
  <div class="virtualized-inventory-table-container">
    <div ref="tableEl" class="inventory-virtual-table">
      <VirtualizedEzTable
        :id="ORDER_MODE_TABLE_CONTENT_ID"
        :items="state.businessList"
        :item-height="ITEM_HEIGHT"
        :container-height="containerHeight"
        :buffer-size="BUFFER_SIZE"
        :grid-template-columns="gridTemplateColumns"
        :sticky-column-indexes="stickyColumnIndexes"
        :loading-status="currentLoadingStatus"
        :expanded-items="expandedItems"
        :get-child-items="getChildItems"
        @toggle="handleToggleOpenBusinesses"
        @scroll="handleScroll"
      >
        <!-- ヘッダー -->
        <template #header>
          <template v-for="(header, index) in fullHeaders" :key="header.key">
            <!-- チェックボックス -->
            <EzHeader v-if="header.key === 'checkbox'" sticky :sticky-offset="0">
              <input type="checkbox" :checked="state.isCheckAllRows" @change="onCheckedAllRows" />
            </EzHeader>
            <!-- ヘッダー編集 -->
            <EzHeader v-else-if="header.key === 'headerModify'" sticky="right">
              <TableHeaderModifyButton :modifiable-header-list="getModifiableHeaderList" @toggle="updateTableHeaderData" />
            </EzHeader>
            <!-- 通常ヘッダー -->
            <EzHeader
              v-else
              :sticky="header.sticky"
              :sticky-offset="calcLeftNumber(index)"
              :use-resizer="header.resizable"
              :sticky-left="header.sticky"
              :sticky-left-position="getLeftPosition(index)"
              :column-index="index"
              :has-right-border="hasHeaderRightBorder(header.key)"
              class="table-header__data"
              @click="changeHeaderSorting(header.key)"
              @resizer="
                (e) => {
                  onMousedownResizer(e, header.key);
                }
              "
            >
              <div class="table-header__grid">
                <span class="table-header__data-label">{{ header.label }}</span>
                <ArrowIcon
                  v-if="InventoryTableService.isHeaderKeySortable(header.key)"
                  :asc="header.key === state.headerSortingState.key ? state.headerSortingState.isAsc : true"
                  @on-click="changeHeaderSorting(header.key)"
                />
              </div>
            </EzHeader>
          </template>
        </template>

        <!-- 最上部固定行 -->
        <template #row-top-sticky>
          <EzCell
            :style="{ gridColumn: `1 / ${headerList.filter((header) => vm.displayHeaders.inventoryControl.includes(header)).length + 2}` }"
            class="table-cell--totalization table-cell--totalization--sticky"
          >
            <div class="table-cell--bold">
              {{ t('合計（取引{number}件全ての数値の合計）', { number: String(state.pagingOptions.totalResultsAvailable) }) }}
            </div>
          </EzCell>
          <template v-for="month in props.transactionMonths" :key="month">
            <TransactionSummaryColumn
              :month="month"
              :data="state.summaryData"
              :header-list="headerList"
              :inventory-vm="inventoryVm"
              :monthly-end-header="getMonthlyEndHeader[month]"
              :order-edit-data="state.orderEditData"
              is-order-mode
            />
          </template>
          <EzCell class="table-cell--totalization" />
        </template>

        <template #row-sticky="{ item, virtualItem, handleToggle: rowHandleToggle }">
          <template v-if="virtualItem.type === 'loading'">
            <EzCell is-highlighted :style="{ gridColumn: '1 / -1' }">
              <div class="table-row--child">
                {{ t('ローディング中...') }}
              </div>
            </EzCell>
          </template>
          <template v-else-if="virtualItem.type === 'error'">
            <EzCell class="table-row--error" :style="{ gridColumn: '1 / -1' }">
              <div class="table-row--error__container">
                <div class="table-row--error__message">
                  {{ t('子情報の取得に失敗しました。再度お試しください。') }}
                </div>
                <div class="table-row--error__button" @click="reloadChildBusiness(virtualItem.parentId)">
                  {{ t('再取得') }}
                </div>
              </div>
            </EzCell>
          </template>
          <template v-for="(header, colIdx) in fullHeaders" v-else :key="header.key">
            <!-- チェックボックスと展開ボタン -->
            <EzCell
              v-if="header.key === 'checkbox'"
              :is-highlighted="isCellHighlighted(virtualItem)"
              :sticky-left="true"
              :sticky-left-position="getLeftPosition(colIdx)"
            >
              <div class="grid-container table-checkbox">
                <div v-if="virtualItem.type === 'parent'">
                  <input v-model="state.checkedAggregateBusinessIds" type="checkbox" :value="item.businessId" />
                </div>
                <div v-if="virtualItem.type === 'parent'">
                  <input :id="item.businessId" class="table-toggle" type="checkbox" @click="rowHandleToggle(item.businessId)" />
                  <label :for="item.businessId" class="table-toggle__label">
                    <SvgLoader
                      v-if="state.isOpeningBusinessId !== item.businessId"
                      class="table-toggle__label--closed"
                      name="chevronRight"
                    />
                    <SvgLoader v-else class="table-toggle__label--opened" name="chevronRight" />
                  </label>
                </div>
              </div>
            </EzCell>
            <!-- 発注詳細列 -->
            <EzCell v-else-if="header.key === 'orderDetail'" :is-highlighted="isCellHighlighted(virtualItem)" class="table-cell">
              <router-link
                v-if="virtualItem.type === 'parent'"
                :to="{ name: 'recommend-order', params: { id: item.businessId } }"
                target="_blank"
              >
                <TextButton class="table-button table-button--detail" button-style="btn-secondary" :on-click="() => {}">
                  {{ t('詳細') }}
                </TextButton>
              </router-link>
            </EzCell>
            <!-- メモ列 -->
            <EzCell
              v-else-if="header.key === 'memo'"
              :is-highlighted="isCellHighlighted(virtualItem)"
              :class="virtualItem.type === 'child' ? 'table-memo-cell table-child-item' : 'table-memo-cell'"
            >
              <!-- 集計取引 -->
              <TableMemoButton
                v-if="virtualItem.type === 'parent'"
                :memo="item.note"
                @save="(memoText) => saveAggregateMemo(memoText, item.businessId)"
                @delete="() => deleteAggregateMemo(item.businessId)"
              />
              <!-- 子取引 -->
              <TableMemoButton
                v-else
                :memo="item.note"
                @save="(memoText) => saveChildMemo(memoText, item.businessId)"
                @delete="() => deleteChildMemo(item.businessId)"
              />
            </EzCell>
            <!-- 基本ヘッダー -->
            <EzCell
              v-else-if="
                headerList.includes(header.key) &&
                !header.key.startsWith('transaction-') &&
                header.key !== 'orderDetail' &&
                header.key !== 'headerModify'
              "
              :is-number="inventoryVm.isNumberHeader(header.key)"
              :is-highlighted="isCellHighlighted(virtualItem)"
              :is-negative="
                inventoryVm.isNumberHeader(header.key) &&
                virtualItem.type === 'parent' &&
                isNegative(inventoryVm.getPropertyFromKey(item, header.key, false))
              "
              :sticky-left="true"
              :sticky-left-position="getLeftPosition(colIdx)"
              :class="
                inventoryVm.isNumberHeader(header.key)
                  ? tableNumberStyleClass(item, state.isOpeningBusinessId, 'virtualized')
                  : tableTextStyleClass(item, state.isOpeningBusinessId, 'virtualized')
              "
            >
              {{ inventoryVm.getPropertyFromKey(item, header.key, virtualItem.type === 'child') ?? '--' }}
            </EzCell>
          </template>
        </template>

        <!-- 行データ -->
        <template #row="{ item, virtualItem }">
          <template v-if="virtualItem.type === 'loading'">
            <EzCell is-highlighted />
          </template>
          <template v-else-if="virtualItem.type === 'error'">
            <EzCell class="table-row--error" />
          </template>
          <template v-for="header in fullHeaders" v-else :key="header.key">
            <!-- 月次データ列 -->
            <OrderMonthlyColumn
              v-if="header.key.startsWith('transaction-') && (virtualItem.type === 'parent' || virtualItem.type === 'child')"
              :header="header"
              :month="getMonthFromKey(header.key)"
              :item="item"
              :virtual-item-type="virtualItem.type"
              :monthly-data="state.monthlyData"
              :order-edit-data="state.orderEditData"
              :hidden-transaction-header-list="state.hiddenTransactionHeaderList"
              :is-opening-business-id="state.isOpeningBusinessId"
              :inventory-vm="inventoryVm"
              :table-number-style-class="tableNumberStyleClass"
              :is-highlighted="isCellHighlighted(virtualItem)"
              :is-negative="isNegative"
              :has-right-border="hasHeaderRightBorder(header.key)"
              :check-nearest-recommend="
                (monthlyData, businessId, month) => {
                  // 近接推奨チェックロジック（必要に応じて実装）
                  return false;
                }
              "
              :get-arrival-status-when-display-button="
                (monthlyData, businessId, month) => {
                  // 到着ステータス取得ロジック（必要に応じて実装）
                  return ArrivalStatus.ApprovalRequested;
                }
              "
              :ensure-order-data="ensureOrderData"
              :find-parent-business-by-id="findParentBusinessById"
              :confirm-order-edit="
                (orderRepository, parentBusiness, month) => {
                  confirmOrderEdit(orderRepository, parentBusiness, month);
                }
              "
              :calc-order-excepted-end="calcOrderExceptedEnd"
              :calc-order-turn-over="calcOrderTurnOver"
              @update-amount="
                (businessId, month, amount) => {
                  ensureOrderData(state.orderEditData, businessId, month);
                  state.orderEditData[businessId][month].amount = amount;
                }
              "
              @update-desired-date="
                (businessId, month, date) => {
                  ensureOrderData(state.orderEditData, businessId, month);
                  state.orderEditData[businessId][month].crossDate = date;
                }
              "
              @update-calendar-flag="
                (businessId, month, flag) => {
                  ensureOrderData(state.orderEditData, businessId, month);
                  state.orderEditData[businessId][month].isEmptyCalendarInputClickedOnce = flag;
                }
              "
              @confirm-order="
                (businessId, month) => {
                  ensureOrderData(state.orderEditData, businessId, month);
                  const parentBusiness = findParentBusinessById(businessId);
                  if (parentBusiness) {
                    const orderRepository = new OrderRepository();
                    confirmOrderEdit(orderRepository, parentBusiness, month);
                  }
                }
              "
            />
            <!-- ヘッダー追加列（取引編集ボタン） -->
            <EzCell v-else-if="header.key === 'headerModify'" :is-highlighted="isCellHighlighted(virtualItem)" class="table-cell">
              <BusinessRowMenu
                is-order-mode
                :business-id="item.businessId"
                @select="async (e) => setBusinessRowMenuStateAndProcessing(e, item, virtualItem.type, virtualItem.parentId)"
              />
            </EzCell>
          </template>
        </template>

        <template #loading>
          <EzTableLoadingState :message="t('データを読み込んでいます')" />
        </template>
        <template #error="{ retry }">
          <EzTableErrorState @retry="retry" />
        </template>
        <template #zero-match>
          <EzTableZeroMatchState />
        </template>
      </VirtualizedEzTable>
    </div>

    <!-- 内示正答率履歴モーダル -->
    <ForecastAccuracyHistoryModal
      :is-open="state.forecastAccuracyModalState.isOpen"
      :history-data="state.forecastAccuracyModalState.historyData ?? null"
      :loading-status="state.forecastAccuracyModalState.loadingState"
      @close="closeForecastAccuracyHistoryModal"
      @change-period="changeForecastAccuracyHistoryPeriod"
      @retry="
        () =>
          state.forecastAccuracyModalState.targetBusinessId &&
          openForecastAccuracyHistoryModal(state.forecastAccuracyModalState.targetBusinessId)
      "
    />
    <CombineOrderModal />
    <CombineOrderConfirmModal />
  </div>
</template>

<style lang="scss" scoped>
@import '@/InventoryControlModule/pages/scss/inventory-table.scss';

.virtualized-inventory-table-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.inventory-virtual-table {
  position: relative;
}

.inventory-table {
  --max-column-width: v-bind('`${MAX_COLUMN_WIDTH}px`');
  --min-column-width: v-bind('`${MIN_COLUMN_WIDTH}px`');
  --loading-grid-column: v-bind(
    '`1 / ${ORDER_HEADER_COLUMN_DEFAULT_NUMBER - getHiddenHeaderNumber(fullHeaders.map(v => v.key), state.hiddenTransactionHeaderList) + 1}`'
  );
}

.table-button--detail {
  width: 50px;
}
</style>

<style lang="scss">
@import '@/InventoryControlModule/pages/scss/vue-datepicker-overrides.scss';
</style>
